export type OverlayBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

export type OverlayFields = {
  north: number | null;
  south: number | null;
  east: number | null;
  west: number | null;
};

export type ShiftDirection = 'north' | 'south' | 'east' | 'west';

export type OverlayCorner = 'nw' | 'ne' | 'se' | 'sw';

export const MAX_LATITUDE = 90;
export const MAX_LONGITUDE = 180;
export const MIN_SPAN = 0.000001;
export const OVERLAY_STEP_PIXELS = 5;

export const stepFromZoom = (
  zoom: number,
  pixels: number = OVERLAY_STEP_PIXELS
) => (360 / (256 * Math.pow(2, zoom))) * pixels;

export const isValidBounds = (bounds: OverlayBounds) =>
  bounds.south < bounds.north &&
  bounds.west < bounds.east &&
  bounds.north <= MAX_LATITUDE &&
  bounds.south >= -MAX_LATITUDE &&
  bounds.east <= MAX_LONGITUDE &&
  bounds.west >= -MAX_LONGITUDE;

export const fieldsToBounds = (fields: OverlayFields): OverlayBounds | null => {
  const {north, south, east, west} = fields;

  if (north === null || south === null || east === null || west === null) {
    return null;
  }

  const bounds = {north, south, east, west};

  return isValidBounds(bounds) ? bounds : null;
};

export const boundsToFields = (bounds: OverlayBounds): OverlayFields => ({
  north: bounds.north,
  south: bounds.south,
  east: bounds.east,
  west: bounds.west,
});

export const toLeafletBounds = (
  bounds: OverlayBounds
): [[number, number], [number, number]] => [
  [bounds.south, bounds.west],
  [bounds.north, bounds.east],
];

export const shiftBounds = (
  bounds: OverlayBounds,
  direction: ShiftDirection,
  step: number
): OverlayBounds => {
  if (direction === 'north') {
    const delta = Math.min(step, MAX_LATITUDE - bounds.north);
    return {
      ...bounds,
      north: bounds.north + delta,
      south: bounds.south + delta,
    };
  }

  if (direction === 'south') {
    const delta = Math.min(step, bounds.south + MAX_LATITUDE);
    return {
      ...bounds,
      north: bounds.north - delta,
      south: bounds.south - delta,
    };
  }

  if (direction === 'east') {
    const delta = Math.min(step, MAX_LONGITUDE - bounds.east);
    return {...bounds, east: bounds.east + delta, west: bounds.west + delta};
  }

  const delta = Math.min(step, bounds.west + MAX_LONGITUDE);

  return {...bounds, east: bounds.east - delta, west: bounds.west - delta};
};

export const resizeBounds = (
  bounds: OverlayBounds,
  corner: OverlayCorner,
  lat: number,
  lng: number
): OverlayBounds => {
  const latitude = Math.min(Math.max(lat, -MAX_LATITUDE), MAX_LATITUDE);
  const longitude = Math.min(Math.max(lng, -MAX_LONGITUDE), MAX_LONGITUDE);

  if (corner === 'nw') {
    return {
      ...bounds,
      north: Math.max(latitude, bounds.south + MIN_SPAN),
      west: Math.min(longitude, bounds.east - MIN_SPAN),
    };
  }

  if (corner === 'ne') {
    return {
      ...bounds,
      north: Math.max(latitude, bounds.south + MIN_SPAN),
      east: Math.max(longitude, bounds.west + MIN_SPAN),
    };
  }

  if (corner === 'se') {
    return {
      ...bounds,
      south: Math.min(latitude, bounds.north - MIN_SPAN),
      east: Math.max(longitude, bounds.west + MIN_SPAN),
    };
  }

  return {
    ...bounds,
    south: Math.min(latitude, bounds.north - MIN_SPAN),
    west: Math.min(longitude, bounds.east - MIN_SPAN),
  };
};

export const cornerLatLng = (
  bounds: OverlayBounds,
  corner: OverlayCorner
): [number, number] => {
  if (corner === 'nw') {
    return [bounds.north, bounds.west];
  }

  if (corner === 'ne') {
    return [bounds.north, bounds.east];
  }

  if (corner === 'se') {
    return [bounds.south, bounds.east];
  }

  return [bounds.south, bounds.west];
};

export const moveBoundsBy = (
  bounds: OverlayBounds,
  latDelta: number,
  lngDelta: number
): OverlayBounds => {
  const latShift = Math.min(
    Math.max(latDelta, -MAX_LATITUDE - bounds.south),
    MAX_LATITUDE - bounds.north
  );
  const lngShift = Math.min(
    Math.max(lngDelta, -MAX_LONGITUDE - bounds.west),
    MAX_LONGITUDE - bounds.east
  );

  return {
    north: bounds.north + latShift,
    south: bounds.south + latShift,
    east: bounds.east + lngShift,
    west: bounds.west + lngShift,
  };
};
